import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL;

const PURPOSE_OPTIONS = [
  { id: 'audit_prep',          label: 'Audit prep (CMS program audit)' },
  { id: 'quarterly_reporting', label: 'Quarterly reporting' },
  { id: 'annual_reporting',    label: 'Annual reporting' },
  { id: 'ad_hoc',              label: 'Ad-hoc / one-off' },
];

export default function NewRequestWizard({ onCancel, onCreated }) {
  const [step, setStep] = useState(1);
  const [fdrs, setFdrs] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [fdrId, setFdrId] = useState('');
  const [title, setTitle] = useState('');
  const [purpose, setPurpose] = useState('quarterly_reporting');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [selectedCatalog, setSelectedCatalog] = useState(new Set());
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/universe-requests/fdrs`, { credentials: 'include' })
      .then(r => r.json()).then(d => setFdrs(d.fdrs || []));
    fetch(`${API}/api/universe-requests/catalog`, { credentials: 'include' })
      .then(r => r.json()).then(d => setCatalog(d.groups || []));
  }, []);

  function toggleCatalog(id) {
    const next = new Set(selectedCatalog);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedCatalog(next);
  }

  async function submit() {
    setSubmitting(true); setError(null);
    try {
      const body = {
        fdr_entity_id: fdrId,
        title,
        purpose,
        catalog_ids: [...selectedCatalog],
        message_to_fdr: message || null,
        reporting_period_start: periodStart || null,
        reporting_period_end: periodEnd || null,
      };
      const res = await fetch(`${API}/api/universe-requests`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Create failed');
      onCreated(data.request);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  const canStep2 = fdrId && title.trim() && purpose;
  const canSubmit = canStep2 && selectedCatalog.size > 0;

  return (
    <div className="max-w-4xl">
      <button onClick={onCancel} className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-4">
        ← Back to requests
      </button>

      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">New universe request</h2>
        <div className="text-sm text-slate-600">
          Send one secure request to an FDR for one or more files — they get a single link, single login, one inbox covering everything you asked for.
        </div>
      </div>

      <ol className="flex gap-2 mb-6 text-xs font-mono uppercase tracking-widest">
        {[1, 2, 3].map(n => (
          <li key={n} className={`px-3 py-1 border ${step >= n ? 'border-red-700 text-red-700' : 'border-slate-300 text-slate-500'}`}>
            Step {n} — {n === 1 ? 'Who' : n === 2 ? 'What' : 'Send'}
          </li>
        ))}
      </ol>

      {step === 1 && (
        <div className="bg-white border border-slate-200 p-6 space-y-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">FDR</label>
            <select value={fdrId} onChange={e => setFdrId(e.target.value)} className="w-full border border-slate-300 px-2 py-1 text-sm">
              <option value="">— Pick an FDR —</option>
              {fdrs.map(f => (
                <option key={f.id} value={f.id}>{f.fdr_name} ({f.contact_email})</option>
              ))}
            </select>
            {fdrs.length === 0 && (
              <div className="text-xs text-amber-700 mt-1">
                No FDRs registered yet. Add one under the <span className="font-semibold">FDRs</span> tab first.
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Q1 2026 grievances + OD universe"
              className="w-full border border-slate-300 px-2 py-1 text-sm"/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Purpose</label>
              <select value={purpose} onChange={e => setPurpose(e.target.value)} className="w-full border border-slate-300 px-2 py-1 text-sm">
                {PURPOSE_OPTIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Period start</label>
                <input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="w-full border border-slate-300 px-2 py-1 text-sm font-mono"/>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Period end</label>
                <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-full border border-slate-300 px-2 py-1 text-sm font-mono"/>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button disabled={!canStep2} onClick={() => setStep(2)}
              className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-semibold">
              Next →
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Pick the universes to request</h3>
          <div className="text-xs text-slate-600 mb-4">
            Select one or more. The FDR sees them all in one portal — single MFA, single upload flow.
          </div>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {catalog.map(group => (
              <div key={group.category} className="border border-slate-200">
                <div className="px-3 py-2 bg-slate-100 text-xs font-mono uppercase tracking-widest text-slate-700">
                  {group.category}
                </div>
                <div>
                  {group.items.map(item => {
                    const isSel = selectedCatalog.has(item.id);
                    return (
                      <label
                        key={item.id}
                        className={`flex items-center gap-3 px-3 py-2 border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${isSel ? 'bg-red-50' : ''}`}
                      >
                        <input type="checkbox" checked={isSel} onChange={() => toggleCatalog(item.id)}/>
                        <div className="flex-1">
                          <div className="text-sm text-slate-900">{item.label}</div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                            {item.consumer_app} · {item.id}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-4">
            <button onClick={() => setStep(1)} className="text-sm text-slate-600 hover:text-slate-900">← Back</button>
            <div className="flex items-center gap-3">
              <span className="text-xs font-mono text-slate-500">{selectedCatalog.size} selected</span>
              <button disabled={selectedCatalog.size === 0} onClick={() => setStep(3)}
                className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-semibold">
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="bg-white border border-slate-200 p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-900">Review and send</h3>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">FDR</div>
          <div className="text-sm">{fdrs.find(f => f.id === fdrId)?.fdr_name}</div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Universes</div>
          <ul className="text-sm list-disc pl-6 text-slate-800">
            {[...selectedCatalog].map(id => {
              const item = catalog.flatMap(g => g.items).find(i => i.id === id);
              return <li key={id}>{item?.label}</li>;
            })}
          </ul>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Message to FDR (optional)</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4}
              placeholder="Plain English — what's this for, when's the deadline, anything they should know."
              className="w-full border border-slate-300 px-2 py-1 text-sm"/>
          </div>
          {error && <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm font-mono">{error}</div>}
          <div className="text-xs text-slate-600">
            Created as a <span className="font-semibold">draft</span>. You can review, edit, or cancel before the invitation email is sent.
          </div>
          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="text-sm text-slate-600 hover:text-slate-900">← Back</button>
            <button disabled={!canSubmit || submitting} onClick={submit}
              className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-6 py-2 text-sm font-semibold">
              {submitting ? 'Creating…' : 'Create draft request'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
